import { supabase } from "@/integrations/supabase/client";

/**
 * Function to send invoice email - temporarily disabled
 */
export const sendInvoiceEmail = async (billId: string, recipientEmail: string) => {
  console.log('📧 Invoice email sending is temporarily disabled');
  return { success: true, message: 'Email sending temporarily disabled' };
};

/**
 * Function to send subscription confirmation email
 */
export const sendSubscriptionConfirmationEmail = async (
  userEmail: string, 
  productName: string,
  planName: string,
  endDate: string | null,
  price: number
) => {
  try {
    console.log('📧 Sending subscription confirmation email to:', userEmail);
    
    const { data, error } = await supabase.functions.invoke('mailer', {
      body: {
        to: userEmail,
        subject: `Your subscription to ${productName} has been confirmed`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
            <h1 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 10px;">Subscription Confirmation</h1>
            
            <p style="font-size: 16px; line-height: 1.5;">Dear Customer,</p>
            
            <p style="font-size: 16px; line-height: 1.5;">Thank you for subscribing to our services. Your subscription has been successfully processed.</p>
            
            <div style="background-color: #f9f9f9; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <h2 style="color: #333; margin-top: 0;">Subscription Details</h2>
              <p><strong>Product:</strong> ${productName}</p>
              <p><strong>Plan:</strong> ${planName}</p>
              <p><strong>Price:</strong> $${price.toFixed(2)}</p>
              ${endDate ? `<p><strong>Valid Until:</strong> ${new Date(endDate).toLocaleDateString()}</p>` : ''}
            </div>
            
            <p style="font-size: 16px; line-height: 1.5;">You can manage your subscription and view billing details by logging into your account.</p>
            
            <p style="font-size: 16px; line-height: 1.5;">If you have any questions or need assistance, please don't hesitate to contact our support team.</p>
            
            <p style="font-size: 16px; line-height: 1.5;">Best regards,<br>The Support Team</p>
          </div>
        `
      }
    });
    
    if (error) {
      console.error('❌ Error sending confirmation email:', error);
      throw error;
    }
    
    console.log('✅ Confirmation email sent successfully:', data);
    return data;
    
  } catch (error) {
    console.error('❌ Error in sendSubscriptionConfirmationEmail:', error);
    // Log but don't fail the entire process if email sending fails
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Function to send admin notification about new subscriptions
 */
export const sendAdminSubscriptionNotification = async (
  schoolId: string,
  productName: string,
  planName: string,
  price: number,
  userEmail: string | null
) => {
  try {
    // First, get the admin email from infradigital_config
    const { data: configData, error: configError } = await supabase
      .from('infradigital_config')
      .select('value')
      .eq('key', 'email_cs')
      .single();
    
    if (configError || !configData) {
      console.error('❌ Error fetching admin email from config:', configError || 'No email_cs found');
      return { success: false, error: 'Admin email not configured' };
    }
    
    const adminEmail = configData.value;
    
    if (!adminEmail) {
      console.error('❌ Admin email (email_cs) not found in infradigital_config');
      return { success: false, error: 'Admin email not configured' };
    }
    
    console.log('📧 Sending admin notification email to:', adminEmail);
    
    // Get the school name for more detailed notification
    const { data: schoolData } = await supabase
      .from('clients')
      .select('nama_sekolah')
      .eq('school_id', schoolId)
      .single();
    
    const schoolName = schoolData?.nama_sekolah || `ID: ${schoolId}`;
    
    // Send notification email to admin
    const { data, error } = await supabase.functions.invoke('mailer', {
      body: {
        to: adminEmail,
        subject: `New Subscription Alert: ${schoolName}`,
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
            <h1 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 10px;">New Subscription Alert</h1>
            
            <p style="font-size: 16px; line-height: 1.5;">A new subscription has been created in the system.</p>
            
            <div style="background-color: #f9f9f9; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <h2 style="color: #333; margin-top: 0;">Subscription Details</h2>
              <p><strong>School:</strong> ${schoolName}</p>
              <p><strong>School ID:</strong> ${schoolId}</p>
              <p><strong>Product:</strong> ${productName}</p>
              <p><strong>Plan:</strong> ${planName}</p>
              <p><strong>Price:</strong> $${price.toFixed(2)}</p>
              ${userEmail ? `<p><strong>User Email:</strong> ${userEmail}</p>` : '<p><strong>User Email:</strong> Not provided (Public subscription)</p>'}
            </div>
            
            <p style="font-size: 16px; line-height: 1.5;">This subscription may require follow-up, especially if it was created through the public subscription interface.</p>
            
            <p style="font-size: 16px; line-height: 1.5;">This is an automated notification. Please log into the admin dashboard to see full details.</p>
          </div>
        `
      }
    });
    
    if (error) {
      console.error('❌ Error sending admin notification email:', error);
      return { success: false, error: error.message };
    }
    
    console.log('✅ Admin notification email sent successfully:', data);
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ Error in sendAdminSubscriptionNotification:', error);
    // Log but don't fail the entire process if email sending fails
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
